import React, { useState, useSyncExternalStore } from 'react';
import DesktopAppbar from './Desktop';
import MobileAppbar from './Mobile';
import { useRouter } from 'next/router';
import { ROUTES } from 'config/Nav';
import { useAuth, darkTheme } from '@elevator/ui';
import { deleteCartMeta } from 'lib/cart';
import { useTheme, useMediaQuery, ThemeProvider } from '@mui/material';

const emptySubscribe = () => () => {};
const useHasMounted = () =>
  useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );

/**
 * Appbar Component
 *
 * Main navigation bar component for the site. Displays site logo, navigation links,
 * search functionality, user authentication controls, and theme toggle.
 *
 * Features:
 * - Responsive design with separate Desktop and Mobile variants
 * - Search input with sanitization and navigation
 * - User authentication state management
 * - Theme toggle (light/dark mode)
 * - Cart management integration
 * - Handles SSR hydration to prevent style insertion order mismatches
 *
 * Used in:
 * - Site layout (wraps all pages)
 *
 * @returns The appropriate Appbar variant (Desktop or Mobile) based on screen size
 */
const Appbar = () => {
  const theme = useTheme();
  const mounted = useHasMounted();
  // Use noSsr: true to prevent SSR evaluation and avoid hydration mismatch
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  const router = useRouter();

  // Tracks the current search input field
  const [searchInput, setSearchInput] = React.useState('');

  const { currentUser, signOut } = useAuth();

  const onSignout = async () => {
    await signOut();
    await deleteCartMeta();
    window.location.replace(ROUTES.HOME.to);
  };

  /**
   * Handler for the current search input value
   */
  const handleSearchInput = (input: string) => {
    setSearchInput(input);
  };

  /**
   * Handler for submiting the search input
   */
  const onSubmitSearchInput = (
    event: React.FormEvent<HTMLFormElement>,
    cb?: () => void,
  ) => {
    event.preventDefault();

    // Sanitize input: trim whitespace and collapse multiple spaces to single space
    const sanitizedInput = searchInput.trim().replace(/\s+/g, ' ');
    setSearchInput(''); // clear input

    // If a callback was defined, trigger
    cb && cb();

    // Only navigate if there's a non-empty search query
    if (sanitizedInput) {
      router.push({
        pathname: ROUTES.SEARCH.to,
        query: {
          s: sanitizedInput,
        },
      });
    }
  };

  const handleSubmitClick = () => {
    router.push(ROUTES.SUBMIT.to);
  };

  const appbarProps = {
    handleSignoutClick: onSignout,
    currentUser,
    searchInput,
    handleSearchInput,
    onSubmitSearchInput,
    handleSubmitClick,
  };

  // Wrap in dark theme so the appbar and its dropdowns always use the dark palette
  const appbarContent = !mounted ? (
    <DesktopAppbar {...appbarProps} />
  ) : isMobile ? (
    <MobileAppbar {...appbarProps} />
  ) : (
    <DesktopAppbar {...appbarProps} />
  );

  return <ThemeProvider theme={darkTheme}>{appbarContent}</ThemeProvider>;
};

export default Appbar;
